<template>
  <div class="TeacherRequestView">
    <button
      type="button"
      class="TeacherRequestView-back"
      @click="goBack"
      :aria-label="$t('teacherRequest.back')"
    >
      <svg
        class="TeacherRequestView-back-icon"
        viewBox="0 0 24 24"
        width="18"
        height="18"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <!-- P43-02: Back is «→» in Persian and «←» in English. -->
        <path
          :d="dir === 'rtl' ? 'M5 12h14m-7-7l7 7-7 7' : 'M19 12H5m7-7l-7 7 7 7'"
        />
      </svg>
      {{ $t('teacherRequest.back') }}
    </button>

    <header class="TeacherRequestView-header">
      <span class="TeacherRequestView-eyebrow">{{
        $t('teacherRequest.eyebrow')
      }}</span>
      <h1 class="TeacherRequestView-title">
        {{ $t('teacherRequest.title') }}
      </h1>
      <p class="TeacherRequestView-subtitle">
        {{ $t('teacherRequest.subtitle') }}
      </p>
    </header>

    <p v-if="hasWaitingPeriod" class="TeacherRequestView-waiting">
      {{ waitingPeriodMessage }}
    </p>

    <loader
      v-if="isFetchingSubjects"
      class="TeacherRequestView-loader uc-column items-center"
      data-testid="teacher-request-loading"
    />

    <div
      v-else-if="fetchingSubjectsError"
      class="TeacherRequestView-error"
      data-testid="teacher-request-error"
    >
      <p>{{ $t('teacherRequest.error') }}</p>
      <large-button primary @click.native="retry">{{
        $t('teacherRequest.retry')
      }}</large-button>
    </div>

    <div
      v-else
      class="TeacherRequestView-cards"
      data-testid="teacher-request-cards"
    >
      <button
        v-for="card in cards"
        :key="card.topic"
        type="button"
        class="TeacherRequestView-card"
        :disabled="isCardDisabled"
        @click="selectTopic(card)"
      >
        <component
          :is="card.svg"
          v-if="isComponentSvg(card.svg)"
          class="TeacherRequestView-card-icon"
          aria-hidden="true"
        />
        <img
          v-else
          :src="card.svg"
          :alt="`${card.topic} icon`"
          class="TeacherRequestView-card-icon"
        />
        <span class="TeacherRequestView-card-title">{{
          displayTitle(card)
        }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import Loader from '@/components/Loader.vue'
import LargeButton from '@/components/LargeButton.vue'
import calculateWaitingPeriodCountdown from '@/utils/calculate-waiting-period-countdown'
import {
  SUPPORTED_TOPICS,
  localizedTopicLabel,
  localizedSubtopicDisplayNames,
} from './subjectLabels'

// P2 -- redesigned student entry point for requesting help from a Borna
// teacher. Renders ONLY what the existing `POST /session/new` contract
// accepts and uses today: a topic + subtopic pair (`subjects/
// sessionRequestTopicCards`). Selecting a topic opens the existing, frozen
// `SubjectSelectionModal`, which owns subtopic selection, the pre-session
// survey and the call to `startSession` -- unchanged. No grade/stage/
// context/upload/timing/credit field exists here because none of those are
// read by the backend for session creation.
export default {
  name: 'teacher-request-view',
  components: { Loader, LargeButton },
  data() {
    return {
      disableCards: false,
      waitingPeriodTimeoutId: null,
      hasWaitingPeriod: false,
      waitingPeriodTimeLeft: 0,
    }
  },
  computed: {
    ...mapState({
      latestSession: state => state.user.latestSession,
      isFetchingSubjects: state => state.subjects.isFetchingSubjects,
      fetchingSubjectsError: state => state.subjects.fetchingSubjectsError,
      subjectsMap: state => state.subjects.subjects,
      user: state => state.user.user,
    }),
    ...mapGetters({
      dir: 'app/direction',
      isSessionAlive: 'user/isSessionAlive',
      topicCards: 'subjects/sessionRequestTopicCards',
    }),
    // Subject catalog remediation: `sessionRequestTopicCards` is the real,
    // unmodified `/api/subjects` catalog (unchanged for every other
    // consumer). This view alone additionally allowlists it down to the
    // topics whose actual subject matter is real, Borna-appropriate content
    // -- see subjectLabels.js for why the other three legacy topics
    // (College Counseling, SAT & ACT Prep, Social Studies/U.S. History) are
    // excluded rather than relabeled.
    cards() {
      return this.topicCards.filter(card =>
        SUPPORTED_TOPICS.includes(card.topic)
      )
    },
    isCardDisabled() {
      return this.disableCards || this.isSessionAlive || !!this.user.isBanned
    },
    waitingPeriodMessage() {
      const countdown = calculateWaitingPeriodCountdown(
        this.waitingPeriodTimeLeft
      )
      const minuteTextFormat = countdown === 1 ? 'minute' : 'minutes'

      if (this.dir === 'ltr')
        return `You must wait at least ${countdown} ${minuteTextFormat} before requesting a new session.`
      else
        return `شما حداقل بایستی ${countdown} دقیقه قبل درخواست یک سشن جدید منتظر بمانید.`
    },
  },
  created() {
    if (
      Object.keys(this.subjectsMap).length === 0 &&
      !this.isFetchingSubjects
    ) {
      this.$store.dispatch('subjects/getSubjects')
    }
    this.checkOrEnforceWaitingPeriod()
  },
  beforeDestroy() {
    clearTimeout(this.waitingPeriodTimeoutId)
  },
  watch: {
    latestSession() {
      this.checkOrEnforceWaitingPeriod()
    },
    isSessionAlive() {
      this.checkOrEnforceWaitingPeriod()
    },
  },
  methods: {
    goBack() {
      this.$router.push(`/${this.$i18n.locale}/dashboard`)
    },
    retry() {
      this.$store.dispatch('subjects/getSubjects')
    },
    isComponentSvg(svg) {
      return typeof svg === 'object'
    },
    displayTitle(card) {
      return localizedTopicLabel(card.topic, card.title, this.$i18n.locale)
    },
    selectTopic(card) {
      if (this.isCardDisabled) return
      this.$store.dispatch('app/modal/show', {
        component: 'SubjectSelectionModal',
        data: {
          backText: 'Dashboard',
          acceptText: this.$t('teacherRequest.subtopicAccept'),
          topic: card.topic,
          title: this.displayTitle(card),
          subtopics: card.subtopics,
          subtopicDisplayNames: localizedSubtopicDisplayNames(
            card.subtopics,
            card.subtopicDisplayNames,
            this.$i18n.locale
          ),
          svg: card.svg,
          preSelectedSubtopic: '',
          subtitle: this.$t('teacherRequest.subtopicSubtitle'),
        },
      })
    },
    calculateTimeSinceLastSession() {
      if (!this.latestSession || !this.latestSession.createdAt) return Infinity
      const sessionCreatedAtInMS = new Date(
        this.latestSession.createdAt
      ).getTime()
      return new Date().getTime() - sessionCreatedAtInMS
    },
    // Mirrors SubjectSelection/index.vue's waiting-period gate so the
    // existing post-session cooldown is preserved from this new entry
    // point. Simplified to a single inline message (no desktop header
    // dispatch) since this is a dedicated route, not the dashboard body.
    checkOrEnforceWaitingPeriod() {
      const timeSinceLastSession = this.calculateTimeSinceLastSession()
      const fiveMinutes = 1000 * 60 * 5
      const timeLeftUntilFiveMinutes = fiveMinutes - timeSinceLastSession

      if (timeSinceLastSession < fiveMinutes && !this.isSessionAlive) {
        this.hasWaitingPeriod = true
        this.disableCards = true
        this.waitingPeriodTimeLeft = timeLeftUntilFiveMinutes

        this.waitingPeriodTimeoutId = setTimeout(() => {
          this.hasWaitingPeriod = false
          this.disableCards = false
        }, timeLeftUntilFiveMinutes)
      } else {
        this.hasWaitingPeriod = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// Local Borna-native tokens, mirroring the accepted role-gate palette
// (DashboardView/index.vue) -- not promoted to global tokens, same
// discipline as MinimalHomeWelcome.vue.
$tr-ink: #071447;
$tr-card-border: #e2e8f0;
$tr-teal-100: #cdf1f8;
$tr-teal-700: #087d9e;

.TeacherRequestView {
  @include flex-container(column);
  max-width: 960px;
  margin: 0 auto;
  padding: 24px 20px 60px;

  @include breakpoint-above('medium') {
    padding: 40px 40px 80px;
  }
}

.TeacherRequestView-back {
  @include flex-container(row, flex-start, center);
  @include child-spacing(left, 6px);
  align-self: flex-start;
  background: none;
  border: none;
  color: $c-secondary-grey;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 24px;
  padding: 4px 0;

  &:hover {
    color: $tr-ink;
  }
}

.TeacherRequestView-header {
  margin-bottom: 28px;
  text-align: left;
}

.TeacherRequestView-eyebrow {
  color: $c-information-blue;
  display: block;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.02em;
  margin-bottom: 8px;
}

.TeacherRequestView-title {
  color: $tr-ink;
  @include font-category('display-small');
  margin: 0 0 8px;

  @include breakpoint-above('medium') {
    @include font-category('display-large');
  }
}

.TeacherRequestView-subtitle {
  color: $c-secondary-grey;
  @include font-category('body');
  margin: 0;
}

.TeacherRequestView-waiting {
  @include font-category('body');
  background: $c-warning-orange;
  border-radius: 8px;
  color: white;
  margin-bottom: 24px;
  padding: 1em;
  text-align: center;
}

.TeacherRequestView-loader {
  margin-top: 20px;
  width: 100%;
}

.TeacherRequestView-error {
  @include flex-container(column, center, center);
  @include child-spacing(top, 16px);
  background: white;
  border: 1px solid $tr-card-border;
  border-radius: 20px;
  padding: 32px 20px;
  text-align: center;

  p {
    color: $c-secondary-grey;
    margin: 0;
  }
}

.TeacherRequestView-cards {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(3, 1fr);

  @include breakpoint-below('huge') {
    grid-template-columns: repeat(2, 1fr);
  }

  @include breakpoint-below('small') {
    grid-template-columns: repeat(1, 1fr);
  }
}

.TeacherRequestView-card {
  @include flex-container(column, center, flex-start);
  @include child-spacing(top, 12px);
  background: white;
  border: 1px solid $tr-card-border;
  border-radius: 20px;
  cursor: pointer;
  padding: 20px;
  text-align: left;

  &:hover:not(:disabled),
  &:focus:not(:disabled) {
    background: $tr-teal-100;
    border-color: $tr-teal-700;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
}

.TeacherRequestView-card-icon {
  height: 48px;
  width: 48px;
}

.TeacherRequestView-card-title {
  color: $tr-ink;
  font-weight: 600;
  @include font-category('body');
}

html.is-rtl {
  .TeacherRequestView-header,
  .TeacherRequestView-card {
    text-align: right;
  }

  .TeacherRequestView-back {
    @include child-spacing-rtl(left, 6px);
  }
}
</style>
